import React, { useCallback, useState, useEffect, useMemo, FC } from 'react'
import FormLayout from 'src/app/layout/form-layout/form-layout'
import { QRCodeSVG } from 'qrcode.react'
import { Grid, Divider, Box } from '@mui/material'
import { Stack, Typography, Button, IconButton, TextField, CircularProgress, Dialog } from '@vapor/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlassPlus, faCopy, faEnvelope, faSquareUpRight, faCheckCircle, faCircleXmark } from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { Link2PayOutput, useSendPosLink2PayEmailMutation, useSendReservationEmailMutation, useTsPayLinkStatusLazyQuery } from 'src/api/api.generated'
import { useInterval } from 'src/utils/useInterval'
import { FormItem, useConfirm } from 'hic-components'
import { useProperty, useTenant } from 'src/app/hooks/context/context.hook'
import { isEmail } from 'src/utils/contact'
import useSuccessSnackBar from 'src/app/hooks/success-snack-bar/use-success-snack-bar'

export type TSPayLink2PayDialogProps = {
  open: boolean
  onClose: (event: Record<string, unknown>, reason: 'backdropClick' | 'escapeKeyDown') => void
  link2Pay: Link2PayOutput
  defaultEmail: string
  onPaymentConfirmed: (success: boolean) => void
}

export const TSPayLink2PayDialog = (props: TSPayLink2PayDialogProps) => {
  const { open, onClose, link2Pay, defaultEmail, onPaymentConfirmed } = props
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const confirm = useConfirm()
  const { id: tenantId } = useTenant()
  const property = useProperty()
  const showSuccessSnackBar = useSuccessSnackBar()

  const link2PayUrl = {
    title: 'TS Pay',
    text: 'Link',
    url: link2Pay.url,
  }

  const canShareLink = navigator?.canShare && navigator?.canShare() && navigator.canShare?.(link2PayUrl)

  const copyLink = useCallback(() => {
    if (canShareLink) {
      navigator.share(link2PayUrl)
    } else {
      navigator.clipboard.writeText(link2Pay.url)
      enqueueSnackbar(t('pms.payment.tsPay.link2pay.copy_link_confirm'), {
        variant: 'success',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'center',
        },
      })
    }
  }, [link2Pay, canShareLink, link2PayUrl])

  const openLink = useCallback(() => {
    window.open(link2Pay.url, 'blank')
  }, [link2Pay.url])

  const [paymentResult, setPaymentResult] = useState<'PENDING' | 'SUCCESS' | 'FAILED'>('PENDING')
  const [error, setError] = useState<string | null | undefined>(null)

  const [delay, setDelay] = useState<number | null>(1000)

  const [checkPayment] = useTsPayLinkStatusLazyQuery({ variables: { tenantId: tenantId, orderKey: link2Pay.orderKey } })

  useInterval(async () => {
    const response = await checkPayment()
    if (response.data?.tsPayLinks?.charges && response.data?.tsPayLinks?.charges.length === 1) {
      const { state, error } = response.data?.tsPayLinks?.charges[0]
      switch (state) {
        case 'DONE':
          setPaymentResult('SUCCESS')
          break
        case 'ERROR':
        case 'FAILED':
          setPaymentResult('FAILED')
          setError(error?.message)
          break
        case 'PENDING':
          setPaymentResult('PENDING')
      }
    }
  }, delay)

  useEffect(() => {
    setPaymentResult('PENDING')
    setDelay(1500)
  }, [link2Pay.url])

  useEffect(() => {
    if (paymentResult !== 'PENDING') {
      setDelay(null)
    }
  }, [paymentResult])

  const [zoom, setZoom] = useState(false)

  const paymentStatus = useMemo(() => {
    switch (paymentResult) {
      case 'PENDING':
        return (
          <Typography variant="body">
            <CircularProgress size={12} sx={{ mx: 1 }} />
            {t('pms.payment.tspay.link2pay.payment_check_status')}
          </Typography>
        )
      case 'SUCCESS':
        return (
          <Stack>
            <FontAwesomeIcon icon={faCheckCircle} color="green" style={{ padding: 5 }} size="5x" />
            <Typography variant="body">{t('pms.payment.tspay.link2pay.payment_received')}</Typography>
          </Stack>
        )
      case 'FAILED':
        return (
          <Stack>
            <FontAwesomeIcon icon={faCircleXmark} color="red" style={{ padding: 5 }} size="5x" />
            <Typography variant="body">{t('pms.payment.tspay.link2pay.payment_failed')}</Typography>
            {error && <Typography variant="caption">{error}</Typography>}
          </Stack>
        )
    }
  }, [paymentResult])

  const toolbar = useMemo(() => {
    const BasicToolbar: FC = ({ children }) => (
      <Grid container>
        <Grid item xs={6}>
          {paymentResult === 'PENDING' && (
            <Button
              color="error"
              variant="outlined"
              onClick={() => {
                confirm({
                  title: t('pms.payment.tsPay.link2pay.dialog.abort_confirm.title'),
                  message: t('pms.payment.tsPay.link2pay.dialog.abort_confirm.description'),
                  confirmButtonText: t('commons.yes'),
                  cancelButtonText: t('commons.no'),
                  onConfirm: () => {
                    onClose({}, 'escapeKeyDown')
                  },
                })
              }}
            >
              {t('commons.cancel')}
            </Button>
          )}
        </Grid>
        <Grid item xs={6} textAlign="end">
          {children}
        </Grid>
      </Grid>
    )

    switch (paymentResult) {
      case 'PENDING':
        return <BasicToolbar />
      case 'SUCCESS':
        return (
          <BasicToolbar>
            <Button variant="contained" onClick={() => onPaymentConfirmed(true)}>
              {t('commons.continue')}
            </Button>
          </BasicToolbar>
        )
      case 'FAILED':
        return (
          <BasicToolbar>
            <Button variant="contained" onClick={() => onPaymentConfirmed(false)}>
              {t('commons.back')}
            </Button>
          </BasicToolbar>
        )
    }
  }, [paymentResult])

  //#region send email
  const [sendPosLink2PayEmail] = useSendPosLink2PayEmailMutation()
  const [email, setEmail] = useState(defaultEmail)
  const isValidEmail = (Boolean(email) && isEmail(email)) ?? false
  const [emailSent, setEmailSent] = useState<boolean>(false)

  const handleSendMail = useCallback(() => {
    if (isValidEmail && property) {
      sendPosLink2PayEmail({
        variables: {
          input: {
            sendTo: email,
            orderKey: link2Pay.orderKey,
            tenantId: tenantId,
            propertyId: property.id,
          },
        },
        onCompleted: () => {
          showSuccessSnackBar(t('pms.payment.tsPay.link2pay.email_sent'))
          setEmailSent(true)
        },
      })
    }
  }, [email, link2Pay.orderKey, tenantId, property])

  //#endregion

  return (
    <>
      <FormLayout
        title={t('pms.payment.tspay.link2pay.dialog_title')}
        appearance={{
          type: 'DIALOG',
          open: open,
          onClose: onClose,
          onBackdropClick(event) {
            event?.stopPropagation()
          },
        }}
        toolbar={toolbar}
      >
        <Box width={{ xs: '100%', md: '800px' }}>
          {paymentResult === 'PENDING' && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs>
                  <Stack justifyContent="space-between" px={1}>
                    <Stack>
                      <Typography variant="subtitle"> {t('pms.payment.tspay.link2pay.option_qrcode.title')}</Typography>
                      <Typography variant="body" mt={2}>
                        {t('pms.payment.tspay.link2pay.option_qrcode.summary')}
                      </Typography>
                    </Stack>
                    <Stack direction="column" alignItems="center" mt={2}>
                      <Stack direction="row" alignItems={'baseline'}>
                        <QRCodeSVG value={link2Pay.url} size={128} bgColor={'#ffffff'} fgColor={'#000000'} level={'L'} includeMargin={false} />
                        <IconButton onClick={() => setZoom(true)}>
                          <FontAwesomeIcon icon={faMagnifyingGlassPlus} />
                        </IconButton>
                      </Stack>
                    </Stack>
                    <Button variant="text" endIcon={<FontAwesomeIcon icon={faSquareUpRight} />} onClick={() => openLink()}>
                      {t('pms.payment.tspay.link2pay.option_qrcode.open_link')}
                    </Button>
                  </Stack>
                </Grid>
                <Divider orientation="vertical" flexItem />
                <Grid item xs>
                  <Stack mr={2} height="100%" justifyContent="space-between">
                    <Stack>
                      <Typography variant="subtitle"> {t('pms.payment.tspay.link2pay.option_link.title')}</Typography>
                      <Typography variant="body" mt={2}>
                        {t('pms.payment.tspay.link2pay.option_link.summary')}
                      </Typography>
                    </Stack>
                    <Button variant="outlined" endIcon={<FontAwesomeIcon icon={faCopy} />} onClick={() => copyLink()}>
                      {t('pms.payment.tspay.link2pay.option_link.button_copy_link')}
                    </Button>
                  </Stack>
                </Grid>
                <Divider orientation="vertical" flexItem />
                <Grid item xs>
                  <Stack mr={2} height="100%" justifyContent="space-between">
                    <Stack>
                      <Typography variant="subtitle"> {t('pms.payment.tspay.link2pay.option_send_mail.title')}</Typography>
                      <Typography variant="body" mt={2}>
                        {t('pms.payment.tspay.link2pay.option_send_mail.summary')}
                      </Typography>
                      <Box mt={2}>
                        <FormItem fullWidth errorMessage={!isValidEmail ? t('commons.required_field') : undefined}>
                          <TextField fullWidth value={email} onChange={(event) => setEmail(event.target.value)}></TextField>
                        </FormItem>

                        {emailSent && (
                          <Typography variant="caption" color="var(--success)">
                            {t('pms.payment.tsPay.link2pay.email_sent')}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                    <Button //
                      variant="outlined"
                      disabled={!isValidEmail}
                      endIcon={<FontAwesomeIcon icon={faEnvelope} />}
                      onClick={() => handleSendMail()}
                    >
                      {t('pms.payment.tspay.link2pay.option_send_mail.button_send')}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          )}
          <Box width="100%" textAlign="center" pt={2}>
            {paymentStatus}
          </Box>
        </Box>
      </FormLayout>
      <Dialog fullScreen open={zoom}>
        <Stack direction="column" justifyContent="center" width="100%" alignItems="center" p={4}>
          <QRCodeSVG value={link2Pay.url} size={300} bgColor={'#ffffff'} fgColor={'#000000'} level={'L'} includeMargin={false} />
          <Button variant="contained" onClick={() => setZoom(false)} sx={{ mt: 2 }}>
            {t('commons.close')}
          </Button>
        </Stack>
      </Dialog>
    </>
  )
}
